/**
 * Pagina Profilo (Andrea 2026-09-29): l'utente cambia il nome con cui compare
 * nella lega, l'immagine del profilo e la password.
 *
 * - nome e immagine: PATCH /auth/me (l'immagine viene compressa qui, 256px)
 * - password: POST /auth/change-password (serve quella attuale)
 */

import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useAuthStore, type User } from "../../lib/auth-store";
import { compressImageToDataUrl } from "../common/imageCompress";
import { MobileShell } from "../shell/MobileShell";
import { AvatarUtente } from "./AvatarUtente";
import { SelettoreTema } from "../common/SelettoreTema";
import { SelettoreLingua } from "../common/SelettoreLingua";
import { GuidaInstallazione } from "../common/InstallaApp";
import { piattaforma } from "../../lib/installazione";
import { type AvatarSalvato, GeneratoreAvatarModal } from "./GeneratoreAvatarModal";
import { SezioneNotifichePush } from "./NotifichePush";
import { tr } from "../../i18n";

const INPUT =
  "w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg focus:outline-none focus:ring-2 focus:ring-accent";
const LABEL = "block text-xs uppercase tracking-wider text-fg-muted mb-1";

function errMsg(e: unknown): string {
  const err = e as {
    response?: { data?: { detail?: string | { msg?: string }[] } };
    message?: string;
  };
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return err.message ?? tr("Errore");
}

type Esito = { ok: boolean; testo: string } | null;

function Messaggio({ esito }: { esito: Esito }) {
  if (!esito) return null;
  return (
    <div className={`text-xs ${esito.ok ? "text-success" : "text-danger"}`}>
      {esito.ok ? "✓ " : "⚠ "}
      {esito.testo}
    </div>
  );
}

export function ProfiloPage() {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);

  const aggiorna = async (dati: DatiProfilo) => {
    const res = await api.patch<User>("/auth/me", dati);
    setUser(res.data);
  };

  // Il profilo arriva da /auth/me (ProtectedRoute lo carica): intanto un
  // segnaposto, non una pagina vuota.
  if (!user) {
    return (
      <MobileShell logoSuffix="PROFILO">
        <div className="max-w-xl mx-auto text-sm text-fg-muted">{tr("Un attimo…")}</div>
      </MobileShell>
    );
  }
  return (
    <MobileShell logoSuffix="PROFILO">
      <div className="max-w-xl mx-auto space-y-3">
        <SezioneImmagine user={user} aggiorna={aggiorna} />
        <SezioneNome user={user} aggiorna={aggiorna} />
        <SezionePassword />
        {/* Andrea 2026-10-08: tema e installazione sul telefono */}
        <div className="card space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("Aspetto")}
          </div>
          <SelettoreTema />
          <div className="text-[11px] text-fg-muted">
            {tr("Automatico segue le impostazioni del telefono (giorno chiaro, notte scuro).")}
          </div>
        </div>
        {/* Andrea 2026-10-09: lingua dell'app (per ora italiano e inglese) */}
        <div className="card space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
            {tr("Lingua")}
          </div>
          <SelettoreLingua />
          <div className="text-[11px] text-fg-muted">
            {tr(
              tr(
                "La lingua vale per i menu e le pagine dell'app; i testi scritti dagli altri restano come sono.",
              ),
            )}
          </div>
        </div>
        {/* Andrea 2026-10-08: solo da telefono/tablet, su desktop non serve */}
        {piattaforma() !== "altro" && (
          <div className="card space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
              {tr("App sul telefono")}
            </div>
            <GuidaInstallazione />
          </div>
        )}
        {/* Andrea 2026-10-16: push sul telefono (mercato, giornata, promemoria, stampa) */}
        <SezioneNotifichePush user={user} />
        <div className="card text-xs text-fg-muted space-y-1">
          <div>
            <span className="uppercase tracking-wider">{tr("Email")}</span> ·{" "}
            <span className="text-fg">{user.email}</span>
          </div>
          <div>{tr("L'email di accesso la cambia l'amministratore della lega.")}</div>
        </div>
      </div>
    </MobileShell>
  );
}

interface DatiProfilo {
  nome_completo?: string;
  avatar_data_url?: string | null;
  /** scelte del generatore avatar; null = immagine caricata da file */
  avatar_config?: Record<string, unknown> | null;
}
type Aggiorna = (dati: DatiProfilo) => Promise<void>;

function SezioneImmagine({ user, aggiorna }: { user: User; aggiorna: Aggiorna }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<Esito>(null);
  const [generatore, setGeneratore] = useState(false);

  // Avatar dal generatore: PNG 512 → 256px compresso, con le scelte fatte
  // (cosi' riaprendolo si ritrova l'avatar com'era).
  const salvaDaGeneratore = async ({ image, stato }: AvatarSalvato) => {
    const blob = await (await fetch(image)).blob();
    const file = new File([blob], "avatar.png", { type: blob.type || "image/png" });
    const dataUrl = await compressImageToDataUrl(file, { maxDim: 256, maxBytes: 120_000 });
    await aggiorna({ avatar_data_url: dataUrl, avatar_config: stato });
    setEsito({ ok: true, testo: tr("Avatar aggiornato") });
  };

  const scegli = async (file: File | undefined) => {
    if (!file) return;
    setInCorso(true);
    setEsito(null);
    try {
      const dataUrl = await compressImageToDataUrl(file, { maxDim: 256, maxBytes: 120_000 });
      await aggiorna({ avatar_data_url: dataUrl, avatar_config: null });
      setEsito({ ok: true, testo: tr("Immagine aggiornata") });
    } catch (e) {
      setEsito({ ok: false, testo: errMsg(e) });
    } finally {
      setInCorso(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const togli = async () => {
    setInCorso(true);
    setEsito(null);
    try {
      await aggiorna({ avatar_data_url: null });
      setEsito({ ok: true, testo: tr("Immagine tolta") });
    } catch (e) {
      setEsito({ ok: false, testo: errMsg(e) });
    } finally {
      setInCorso(false);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
        {tr("Immagine del profilo")}
      </div>
      <div className="flex items-center gap-4">
        <AvatarUtente user={user} size={80} />
        <div className="flex flex-col gap-2 min-w-0">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => scegli(e.target.files?.[0])}
          />
          <button
            type="button"
            className="btn-primary disabled:opacity-50"
            disabled={inCorso}
            onClick={() => setGeneratore(true)}
          >
            {user.avatar_config ? tr("✏️ Modifica il tuo avatar") : tr("✏️ Crea il tuo avatar")}
          </button>
          <button
            type="button"
            className="btn-ghost disabled:opacity-50"
            disabled={inCorso}
            onClick={() => inputRef.current?.click()}
          >
            {inCorso ? tr("Salvo…") : tr("Carica una foto")}
          </button>
          {user.avatar_data_url && (
            <button
              type="button"
              className="btn-ghost disabled:opacity-50"
              disabled={inCorso}
              onClick={togli}
            >
              {tr("Togli immagine")}
            </button>
          )}
        </div>
      </div>
      <Messaggio esito={esito} />
      {generatore && (
        <GeneratoreAvatarModal
          statoIniziale={user.avatar_config}
          onSalva={salvaDaGeneratore}
          onClose={() => setGeneratore(false)}
        />
      )}
    </div>
  );
}

function SezioneNome({ user, aggiorna }: { user: User; aggiorna: Aggiorna }) {
  const [nome, setNome] = useState(user.nome_completo);
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<Esito>(null);
  useEffect(() => setNome(user.nome_completo), [user.nome_completo]);

  const pulito = nome.trim().replace(/\s+/g, " ");
  const cambiato = pulito !== user.nome_completo;

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pulito.length < 2) {
      setEsito({ ok: false, testo: tr("Nome troppo corto") });
      return;
    }
    setInCorso(true);
    setEsito(null);
    try {
      await aggiorna({ nome_completo: pulito });
      setEsito({ ok: true, testo: tr("Nome aggiornato") });
    } catch (err) {
      setEsito({ ok: false, testo: errMsg(err) });
    } finally {
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={salva} className="card space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent">{tr("Nome")}</div>
      <div>
        <label className={LABEL} htmlFor="profilo-nome">
          {tr("Come compari nella lega")}
        </label>
        <input
          id="profilo-nome"
          className={INPUT}
          value={nome}
          maxLength={80}
          onChange={(e) => setNome(e.target.value)}
        />
      </div>
      <button
        type="submit"
        className="btn-primary disabled:opacity-50 w-full"
        disabled={inCorso || !cambiato}
      >
        {inCorso ? tr("Salvo…") : tr("Salva nome")}
      </button>
      <Messaggio esito={esito} />
    </form>
  );
}

function SezionePassword() {
  const [vecchia, setVecchia] = useState("");
  const [nuova, setNuova] = useState("");
  const [conferma, setConferma] = useState("");
  const [inCorso, setInCorso] = useState(false);
  const [esito, setEsito] = useState<Esito>(null);

  const salva = async (e: React.FormEvent) => {
    e.preventDefault();
    if (nuova.length < 8) {
      setEsito({ ok: false, testo: tr("La nuova password deve avere almeno 8 caratteri") });
      return;
    }
    if (nuova !== conferma) {
      setEsito({ ok: false, testo: tr("Le due password nuove non coincidono") });
      return;
    }
    setInCorso(true);
    setEsito(null);
    try {
      await api.post("/auth/change-password", {
        vecchia_password: vecchia,
        nuova_password: nuova,
        // cosi' questo dispositivo resta collegato, gli altri escono
        refresh_token: useAuthStore.getState().refreshToken,
      });
      setVecchia("");
      setNuova("");
      setConferma("");
      setEsito({ ok: true, testo: tr("Password cambiata") });
    } catch (err) {
      setEsito({ ok: false, testo: errMsg(err) });
    } finally {
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={salva} className="card space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-wider text-accent">
        {tr("Password")}
      </div>
      <div>
        <label className={LABEL} htmlFor="pwd-vecchia">
          {tr("Password attuale")}
        </label>
        <input
          id="pwd-vecchia"
          type="password"
          autoComplete="current-password"
          className={INPUT}
          value={vecchia}
          onChange={(e) => setVecchia(e.target.value)}
          required
        />
      </div>
      <div>
        <label className={LABEL} htmlFor="pwd-nuova">
          {tr("Nuova password (almeno 8 caratteri)")}
        </label>
        <input
          id="pwd-nuova"
          type="password"
          autoComplete="new-password"
          className={INPUT}
          value={nuova}
          onChange={(e) => setNuova(e.target.value)}
          required
        />
      </div>
      <div>
        <label className={LABEL} htmlFor="pwd-conferma">
          {tr("Ripeti la nuova password")}
        </label>
        <input
          id="pwd-conferma"
          type="password"
          autoComplete="new-password"
          className={INPUT}
          value={conferma}
          onChange={(e) => setConferma(e.target.value)}
          required
        />
      </div>
      <button type="submit" className="btn-primary disabled:opacity-50 w-full" disabled={inCorso}>
        {inCorso ? "Salvo…" : "Cambia password"}
      </button>
      <Messaggio esito={esito} />
    </form>
  );
}
